import axios from 'axios';
import { getUrl } from '../service-utils';
import authHeader from '../auth-header';

const BASE = `${getUrl()}admin/analytics`;

// ── Types ─────────────────────────────────────────────────────────────────

export interface DayCount {
  date: string;
  count: number;
}

export interface UsersAnalytics {
  dau: number;
  wau: number;
  mau: number;
  totalUsers: number;
  newSignupsByDay: DayCount[];
  activeUsersByDay: DayCount[];
}

export interface SearchEndpointStat {
  endpoint: string;
  count: number;
  converted: number;
  avgResultCount: number;
  conversionRate: number;
}

export interface TopQuery {
  query: string;
  count: number;
}

export interface SearchDayStat {
  date: string;
  count: number;
  conversions: number;
}

export interface SearchFunnelAnalytics {
  totalSearches: number;
  totalConversions: number;
  conversionRate: number;
  byEndpoint: SearchEndpointStat[];
  topQueries: TopQuery[];
  byDay: SearchDayStat[];
}

export interface ContentDayStat {
  date: string;
  mutations: number;
}

export interface ContentEndpointStat {
  endpoint: string;
  method: string;
  count: number;
}

export interface ContentAnalytics {
  tripCreations: number;
  activityAdditions: number;
  byDay: ContentDayStat[];
  topEndpoints: ContentEndpointStat[];
}

export interface AiProviderStat {
  provider: string;
  totalTokens: number;
  totalCostUsd: number;
  calls: number;
}

export interface AiFeatureStat {
  feature: string;
  totalTokens: number;
  totalCostUsd: number;
  calls: number;
}

export interface AiDayStat {
  date: string;
  totalTokens: number;
  totalCostUsd: number;
  calls: number;
}

export interface AiUsageAnalytics {
  totalTokens: number;
  totalCostUsd: number;
  totalCalls: number;
  byProvider: AiProviderStat[];
  byFeature: AiFeatureStat[];
  byDay: AiDayStat[];
}

export interface ApiEndpointStat {
  endpoint: string;
  count: number;
  avgLatencyMs: number;
  errors: number;
  errorRate: number;
}

export interface ApiStatusStat {
  statusClass: string;
  count: number;
}

export interface ApiDayStat {
  date: string;
  count: number;
  avgLatencyMs: number;
  errors: number;
}

export interface ApiRequestsAnalytics {
  totalRequests: number;
  avgLatencyMs: number;
  errorRate: number;
  statusBreakdown: ApiStatusStat[];
  byDay: ApiDayStat[];
  topEndpoints: ApiEndpointStat[];
}

export interface TranscriptionFailureReason {
  reason: string;
  count: number;
}

export interface TranscriptionFailingVideo {
  sourceId: string;
  videoId: string;
  title?: string;
  url?: string;
  failures: number;
  lastError: string;
  attemptedTiers: string[];
  lastAttemptAt?: string;
}

export interface TranscriptionDayStat {
  date: string;
  failures: number;
}

export interface TranscriptionAnalytics {
  totalFailures: number;
  uniqueVideos: number;
  topFailingVideos: TranscriptionFailingVideo[];
  failureReasons: TranscriptionFailureReason[];
  failuresByDay: TranscriptionDayStat[];
}

// ── Service ───────────────────────────────────────────────────────────────

export const adminAnalyticsService = {
  async getUsers(days = 30): Promise<UsersAnalytics> {
    const res = await axios.get<{ status: string; data: UsersAnalytics }>(
      `${BASE}/users`,
      { headers: authHeader(), params: { days } }
    );
    return res.data.data;
  },

  async getSearchFunnel(days = 30): Promise<SearchFunnelAnalytics> {
    const res = await axios.get<{
      status: string;
      data: SearchFunnelAnalytics;
    }>(`${BASE}/search-funnel`, { headers: authHeader(), params: { days } });
    return res.data.data;
  },

  async getContent(days = 30): Promise<ContentAnalytics> {
    const res = await axios.get<{ status: string; data: ContentAnalytics }>(
      `${BASE}/content`,
      { headers: authHeader(), params: { days } }
    );
    return res.data.data;
  },

  async getAiUsage(days = 30): Promise<AiUsageAnalytics> {
    const res = await axios.get<{ status: string; data: AiUsageAnalytics }>(
      `${BASE}/ai-usage`,
      { headers: authHeader(), params: { days } }
    );
    return res.data.data;
  },

  async getApiRequests(days = 7): Promise<ApiRequestsAnalytics> {
    const res = await axios.get<{
      status: string;
      data: ApiRequestsAnalytics;
    }>(`${BASE}/api-requests`, { headers: authHeader(), params: { days } });
    return res.data.data;
  },

  async getTranscription(days = 30): Promise<TranscriptionAnalytics> {
    const res = await axios.get<{
      status: string;
      data: TranscriptionAnalytics;
    }>(`${BASE}/transcription`, { headers: authHeader(), params: { days } });
    return res.data.data;
  },

  async retryYouTubeTier3(sourceId: string, reason?: string): Promise<void> {
    await axios.post(
      `${getUrl()}admin/ingestion/youtube/${sourceId}/retry-tier3`,
      { reason },
      { headers: authHeader() }
    );
  }
};
